import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { PanelsInterfaceComponent } from '../panels-interface.component';
import { Property } from '../../../../../projects/projects.types';
import { Account, FieldDef } from '@core/models';
import { PanelDef } from '@shared/components/panels/panels.types';
import { TsButtonModule, TSIconModule, TsSnackbarService } from 'tsui';
import { TsEmptyStateComponent } from 'tsui/lib/ts-empty-state/ts-empty-state.component';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { BehaviorSubject, EMPTY, filter, Observable, switchMap } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { AssetManagementMessages } from '../../../../../asset-management/utils/asset-management-messages.utils';
import { Store } from '@ngxs/store';
import { ChartRendererComponent } from '../../../../../asset-management/components/visualization-renderer/chart-renderer.component';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  ChartDataSource,
  ChartFieldOption,
  ChartPanelBuilderDialogData,
  ChartVisualizationConfig,
  ReportPeriod,
  AllowedTimeSeriesTypes,
} from '../../../../../asset-management/models';
import { ChartPanelBuilderDialogComponent } from '../../../../../asset-management/components/chart-panel-builder-dialog/chart-panel-builder-dialog.component';
import { TsPanelComponent } from 'tsui/lib/ts-panel/ts-panel.component';
import { Router } from '@angular/router';
import { ChartRendererUtils } from '../../../../../asset-management/utils/chart-renderer.utils';
import { RelatedToType } from '@shared/enums';
import { AssetManagementService } from '../../../../../asset-management/services/asset-management.service';
import { CategoryAccountUtils } from '../../../../../asset-management/utils/category-account.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PropertiesService } from 'app/modules/properties/properties.service';

@Component({
  selector: 'property-chart-panel',
  standalone: true,
  imports: [
    TSIconModule,
    TsEmptyStateComponent,
    ChartRendererComponent,
    TsButtonModule,
    NgClass,
    MatButtonModule,
    TsPanelComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './property-chart-panel.component.html',
  styleUrl: './property-chart-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyChartPanelComponent implements PanelsInterfaceComponent, OnInit {
  // TODO: how to extend this to afford multiple properties?
  @Input() property: Property;
  @Input() account: Account;
  @Input() fieldDefs: FieldDef[];
  @Input() panelDef: PanelDef<ChartVisualizationConfig>;
  @Input() data: any;
  @Input() canEditPanelDef: boolean = true;
  @Output() onUpdate: EventEmitter<string> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();

  readonly createNewChartConfig: { label: string; actionFn: () => void } = {
    label: 'Create New Chart',
    actionFn: () => {
      this.openPanelCreationDialog();
    },
  };

  readonly dialog = inject(Dialog);
  readonly store = inject(Store);
  readonly snackbar = inject(TsSnackbarService);
  readonly router = inject(Router);
  readonly assetManagementService = inject(AssetManagementService);
  readonly propertiesService = inject(PropertiesService);

  readonly onRelatedToChanged = new BehaviorSubject<RelatedToType>(this.panelRelatedTo);
  readonly chartPanelFieldOptions$ = new BehaviorSubject<ChartFieldOption[]>([]);
  readonly destroyRef = inject(DestroyRef);

  readonly chartPanelDataSource = signal(undefined);
  readonly loadingChart = signal(false);

  get hasPanelData(): boolean {
    // checks if the object isn't empty
    return this.panelDef?.meta && Object.keys(this.panelDef.meta).length > 0;
  }

  get panelVisualizationConfig(): ChartVisualizationConfig {
    return this.panelDef?.meta;
  }

  get panelRelatedTo(): RelatedToType {
    return this.panelVisualizationConfig?.related_to;
  }

  get panelReportPeriod(): ReportPeriod {
    return this.panelVisualizationConfig?.reportPeriod;
  }

  get panelFieldsIds(): (number | string)[] {
    return this.panelVisualizationConfig?.series.map((series) => series.fieldId);
  }

  // hack: Overview and tabs have diff grid implementations, since some panel layouts behave differently
  // this is needed.
  get isOverviewTab() {
    // sometimes overview tab is just 'details' on the url 🤷🏻
    return this.router.url.includes('overview') || !this.router.url.includes('tabs');
  }

  ngOnInit() {
    this.onRelatedToChanged
      .pipe(
        filter((relatedTo) => !!relatedTo),
        switchMap((relatedTo) => {
          return this.getOptionsFor(relatedTo);
        }),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((options: ChartFieldOption[]) => {
        this.chartPanelFieldOptions$.next(options);
      });
    this.loadChartDataSource();
  }

  openPanelCreationDialog(chartVisualizationConfig?: ChartVisualizationConfig): void {
    const dialogRef: DialogRef = this.dialog.open(ChartPanelBuilderDialogComponent, {
      ...TsDialogComponent.getDefaultDialogConfig(),
      maxWidth: '1200px',
      disableClose: true,
      data: <ChartPanelBuilderDialogData>{
        fieldOptions: this.chartPanelFieldOptions$,
        relatedToChanged: this.onRelatedToChanged,
        dataSource: (newChartVisualizationConfig: ChartVisualizationConfig): Observable<ChartDataSource> => {
          const fieldIds = newChartVisualizationConfig.series.map((s) => s.fieldId);

          if (newChartVisualizationConfig.related_to === RelatedToType.AssetManagement) {
            return this.getCategoriesChartDataSource$(newChartVisualizationConfig, fieldIds);
          }

          return this.getPropertyChartDataSource$(newChartVisualizationConfig, fieldIds);
        },
        ...(chartVisualizationConfig && { chartVisualizationConfig }),
      },
    });

    dialogRef.closed
      .pipe(
        filter((result) => !!result),
        take(1),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          AssetManagementMessages.Success.CREATE('Panel'),
          AssetManagementMessages.Error.CREATE('Panel'),
        ),
      )
      .subscribe((result: ChartVisualizationConfig) => {
        this.onEditPanelDef.emit({
          ...this.panelDef,
          meta: { ...result },
        });
      });
  }

  private getCategoriesChartDataSource$(
    newChartVisualizationConfig: ChartVisualizationConfig,
    fieldIds: (number | string)[],
  ) {
    return this.assetManagementService
      .getCategoryBalances(newChartVisualizationConfig.reportPeriod, fieldIds, {
        related_to_id: this.property.id,
        related_to_type: RelatedToType.Property,
      })
      .pipe(
        map(({ results, report_period_range }) => {
          return ChartRendererUtils.convertCategoryBalanceToChartData(results, report_period_range);
        }),
        take(1),
      );
  }

  private getPropertyChartDataSource$(
    newChartVisualizationConfig: ChartVisualizationConfig,
    fieldIds: (number | string)[],
  ) {
    return this.propertiesService
      .getPropertyTimeSeriesChartData(newChartVisualizationConfig.reportPeriod, <string[]>fieldIds, this.property.id)
      .pipe(
        map(({ results, period_range }) => {
          return ChartRendererUtils.convertPropertyFieldsToChartData(results, period_range);
        }),
        take(1),
      );
  }

  private getOptionsFor(source: RelatedToType): Observable<ChartFieldOption[]> {
    if (source === RelatedToType.AssetManagement) {
      return this.assetManagementService
        .getCategoryAccounts()
        .pipe(map(CategoryAccountUtils.generateCategoryAccountOptions));
    }

    return this.propertiesService.getPropertyTimeSeriesFieldOptions().pipe(
      map((fieldDefs) => {
        return fieldDefs.map((fieldDef) => {
          return {
            label: fieldDef.label,
            value: fieldDef.name,
            format: <AllowedTimeSeriesTypes>fieldDef?.type || 'number',
          };
        });
      }),
    );
  }

  private loadChartDataSource() {
    if (!this.panelRelatedTo) {
      return;
    }

    this.loadingChart.set(true);

    if (this.panelRelatedTo === RelatedToType.AssetManagement) {
      this.loadCategoriesChartData(this.panelReportPeriod, <number[]>this.panelFieldsIds, this.property.id);
    } else {
      this.loadPropertyChartData(this.panelReportPeriod, <string[]>this.panelFieldsIds, this.property.id);
    }
  }

  private loadPropertyChartData(reportPeriod: ReportPeriod, fieldIds: string[], propertyId: number) {
    this.propertiesService
      .getPropertyTimeSeriesChartData(reportPeriod, fieldIds, propertyId)
      .pipe(take(1))
      .subscribe((results) => {
        this.chartPanelDataSource.set(
          ChartRendererUtils.convertPropertyFieldsToChartData(results.results, results.period_range),
        );
        this.loadingChart.set(false);
      });
  }

  private loadCategoriesChartData(reportPeriod: ReportPeriod, fieldIds: number[], propertyId: number) {
    this.assetManagementService
      .getCategoryBalances(reportPeriod, fieldIds, {
        related_to_id: propertyId,
        related_to_type: RelatedToType.Property,
      })
      .pipe(take(1))
      .subscribe(({ results, report_period_range }) => {
        this.chartPanelDataSource.set(
          ChartRendererUtils.convertCategoryBalanceToChartData(results, report_period_range),
        );
        this.loadingChart.set(false);
      });
  }
}
